import React, { useEffect, useMemo, useState } from 'react';
import styles from '../../styles/ReporteSegurosEstudiantesStyles/ReporteSegurosEstudiantes.module.css';
import { getCampus } from '../../api/campus';
import { getSeccionesAcademicas } from '../../api/secciones_academicas';
import { getPrograms } from '../../api/programs';

/**
 * Filtros del Reporte de Seguros de Estudiantes.
 *
 * Props:
 *  - filters: { id_campus, id_seccion, id_programa }
 *  - onChange(key, value)
 *  - onGenerate()
 *  - loading: boolean
 *  - onContextResolved({ campus, seccion, programa })
 */
function FiltrosReporteSeguros({ filters, onChange, onGenerate, loading, onContextResolved }) {
  const [campusList, setCampusList] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [programas, setProgramas] = useState([]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      getCampus().catch(() => []),
      getSeccionesAcademicas().catch(() => []),
      getPrograms().catch(() => []),
    ]).then(([cam, sec, prg]) => {
      if (!mounted) return;
      setCampusList(Array.isArray(cam) ? cam : []);
      setSecciones(Array.isArray(sec) ? sec : []);
      setProgramas(Array.isArray(prg) ? prg : []);
    });
    return () => { mounted = false; };
  }, []);

  // Programa dinámico: filtrar por sección si está seleccionada.
  // NOTA: NO filtramos por campus aquí porque el serializer expone un único
  // `campus_id` (primera relación activa) por programa, lo que oculta
  // programas que pertenecen a varios campus. La sección académica es la
  // relación canónica del programa.
  const programasFiltrados = useMemo(() => {
    let lista = programas;
    if (filters.id_seccion) {
      lista = lista.filter(
        (p) => p.sectionId !== null && String(p.sectionId) === String(filters.id_seccion)
      );
    }
    return lista.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [programas, filters.id_seccion]);

  // Resetear programa si deja de estar disponible
  useEffect(() => {
    if (!filters.id_programa) return;
    const sigueDisponible = programasFiltrados.some(
      (p) => String(p.id) === String(filters.id_programa)
    );
    if (!sigueDisponible) {
      onChange('id_programa', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programasFiltrados]);

  const resolveContext = () => {
    const campus = campusList.find((c) => String(c.id) === String(filters.id_campus));
    const seccion = secciones.find((s) => String(s.id) === String(filters.id_seccion));
    const programa = programas.find((p) => String(p.id) === String(filters.id_programa));
    return {
      campus: campus ? campus.nombre : 'Todos',
      seccion: seccion ? seccion.nombre : 'Todas',
      programa: programa ? programa.name : 'Todos',
    };
  };

  const handleGenerate = () => {
    if (typeof onContextResolved === 'function') {
      onContextResolved(resolveContext());
    }
    onGenerate();
  };

  const disabled = loading;

  return (
    <section className={styles.filtersSection}>
      <div className={styles.filtersRow}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Campus</label>
          <select
            className={styles.filterSelect}
            value={filters.id_campus || ''}
            onChange={(e) => onChange('id_campus', e.target.value)}
          >
            <option value="">Todos</option>
            {campusList.map((c) => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Sección</label>
          <select
            className={styles.filterSelect}
            value={filters.id_seccion || ''}
            onChange={(e) => onChange('id_seccion', e.target.value)}
          >
            <option value="">Todas</option>
            {secciones.map((s) => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Programa</label>
          <select
            className={styles.filterSelect}
            value={filters.id_programa || ''}
            onChange={(e) => onChange('id_programa', e.target.value)}
          >
            <option value="">Todos</option>
            {programasFiltrados.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup} style={{ flex: '0 0 auto', minWidth: 'auto' }}>
          <label className={styles.filterLabel}>&nbsp;</label>
          <button
            type="button"
            className={styles.generateButton}
            onClick={handleGenerate}
            disabled={disabled}
          >
            {loading ? 'Generando...' : 'Generar Reporte'}
          </button>
        </div>
      </div>
    </section>
  );
}

export default FiltrosReporteSeguros;
